import type { FinancialSnapshotDocument, SnapshotSubCategoryData } from '../financial-snapshot.model';

const GUID = '4a8ba267-741b-41bd-9a82-5fa45670fbe6';
const TS = '2026-07-21T11:00:00.000Z';

function sub(value: number, extras: Partial<SnapshotSubCategoryData> = {}): SnapshotSubCategoryData {
  return {
    value,
    lastEditedAt: TS,
    lastEditedBy: 'client',
    lastEditedById: GUID,
    ...extras,
  };
}

/** Partial in-progress (no submittedDate) — income + some spending + savings. */
export const IN_PROGRESS_SNAPSHOT_FIXTURE: FinancialSnapshotDocument = {
  guid: GUID,
  createdDate: '2026-07-21T10:00:00.000Z',
  modifiedDate: '2026-07-21T14:30:00.000Z',
  lastUpdatedAt: '2026-07-21T14:30:00.000Z',
  categories: {
    monthlyhouseholdincome: {
      subCategories: {
        takehomepay: sub(4500),
        otherhouseholdtakehomepay: sub(800),
        otherincome: sub(0),
      },
    },
    housing: {
      subCategories: {
        rentmortgage: sub(1900),
        homerenterinsurance: sub(125),
        hoafees: sub(0),
      },
    },
    utilities: {
      subCategories: {
        electricgas: sub(200),
        watersewer: sub(80),
        cableinternet: sub(100),
        cellphone: sub(90),
      },
    },
    transportation: {
      subCategories: {
        carpayments: sub(350),
        fuel: sub(180),
        carinsurance: sub(120),
        publictransportation: sub(0),
      },
    },
    savings: {
      totalValue: 300,
      lastEditedAt: '2026-07-21T14:30:00.000Z',
      lastEditedBy: 'client',
      lastEditedById: GUID,
    },
  },
};

/** Complete: submittedDate recent, fuller categories. */
export const COMPLETE_SNAPSHOT_FIXTURE: FinancialSnapshotDocument = {
  ...IN_PROGRESS_SNAPSHOT_FIXTURE,
  submittedDate: '2026-07-21T10:05:00.000Z',
  categories: {
    ...IN_PROGRESS_SNAPSHOT_FIXTURE.categories,
    food: {
      subCategories: {
        groceries: sub(400),
        restaurantscoffee: sub(150),
        takeout: sub(80),
      },
    },
    dependentcare: {
      subCategories: {
        childsupport: sub(0),
        daycare: sub(200),
        petexpenses: sub(40),
      },
    },
    subscriptions: {
      subCategories: {
        gamingaudiobooks: sub(15),
        videostreamingservices: sub(45),
        gymmembership: sub(30),
      },
    },
    debtrepayment: {
      subCategories: {
        personalloans: sub(100),
        studentloans: sub(250),
        creditcards: sub(200),
      },
    },
    other: {
      subCategories: {
        activities: sub(50),
        medical: sub(75),
        personal: sub(60),
        custom_other: sub(25, { label: 'Other' }),
      },
    },
  },
};

/** Outdated: submittedDate > 90 days ago. */
export const OUTDATED_SNAPSHOT_FIXTURE: FinancialSnapshotDocument = {
  ...COMPLETE_SNAPSHOT_FIXTURE,
  submittedDate: '2025-01-01T10:00:00.000Z',
  createdDate: '2025-01-01T09:00:00.000Z',
  modifiedDate: '2025-01-15T10:00:00.000Z',
  lastUpdatedAt: '2025-01-15T10:00:00.000Z',
};

/** Blank vs 0 edge: one subcategory explicit 0, sibling unset. */
export const BLANK_VS_ZERO_SNAPSHOT_FIXTURE: FinancialSnapshotDocument = {
  guid: GUID,
  createdDate: '2026-07-21T10:00:00.000Z',
  categories: {
    monthlyhouseholdincome: {
      subCategories: {
        takehomepay: sub(5000),
        otherincome: sub(0),
      },
    },
    housing: {
      subCategories: {
        rentmortgage: sub(0),
      },
    },
  },
};

/** No income entered — metrics % / net should be null after recompute. */
export const NO_INCOME_SNAPSHOT_FIXTURE: FinancialSnapshotDocument = {
  guid: GUID,
  createdDate: '2026-07-21T10:00:00.000Z',
  categories: {
    housing: {
      subCategories: {
        rentmortgage: sub(1000),
      },
    },
  },
};

/** Complete-like snapshot with multiple client custom Other entries (distinct keys). */
export const MULTI_CUSTOM_OTHER_SNAPSHOT_FIXTURE: FinancialSnapshotDocument = {
  ...COMPLETE_SNAPSHOT_FIXTURE,
  categories: {
    ...COMPLETE_SNAPSHOT_FIXTURE.categories,
    other: {
      subCategories: {
        activities: sub(50),
        medical: sub(75),
        personal: sub(60),
        '0': sub(40, { label: 'Gifts' }),
        donations: sub(15, { label: 'Donations' }),
      },
    },
  },
};
